/**
 * Axis and label maths for the hand-rolled money charts on the Reports surface.
 *
 * Extracted from `BillingsByMonthChart` when the two new delivery series pushed
 * that panel past the 500-line review tripwire. The seam is real rather than
 * arithmetic: everything here is a **pure function of a figure and a formatter**
 * with no theme, no palette and no JSX, whereas what stays behind is entirely
 * layout. It is also the half worth unit-testing directly — a wrong axis
 * ceiling clips a series silently, and `£NaN` ticks have shipped before from a
 * ceiling computed over an empty array.
 */

/**
 * X-axis label for a `YYYY-MM` bucket.
 *
 * January carries a year suffix so a span crossing a year boundary reads
 * unambiguously; the other eleven months do not, because a repeated year on
 * every tick is noise at 0.7rem.
 */
export function monthLabel(monthISO: string): string {
  const [y, m] = monthISO.split('-').map(Number);
  if (!y || !m) return monthISO;
  const short = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'short',
    timeZone: 'UTC',
  });
  return m === 1 ? `${short} ’${String(y).slice(2)}` : short;
}

/** Round an axis ceiling up to a visually-tidy number: 1, 2, 5 × 10^n. */
export function niceCeiling(rawMax: number): number {
  if (rawMax <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rawMax));
  const normalised = rawMax / magnitude;
  if (normalised <= 1) return magnitude;
  if (normalised <= 2) return 2 * magnitude;
  if (normalised <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

/**
 * Tidy step sizes, as multiples of a power of ten.
 *
 * Finer than {@link niceCeiling}'s 1 / 2 / 5, and that is the point: those three
 * are the right vocabulary for a ceiling, but too coarse for one computed from a
 * tick. A maximum of 2,201 over four intervals wants a step near 550, and 1/2/5
 * rounds that to 1,000 — a 4,000 axis whose bars fill the bottom half of the
 * plot, with the reader shown mostly empty space.
 */
const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

/**
 * An axis ceiling that both **clears the data** and **divides into tidy ticks**.
 *
 * The step is rounded up to a {@link NICE_STEPS} value and the ceiling is that
 * step times the interval count, so every tick lands on a round number and the
 * top of the axis sits just above the tallest mark rather than a power of ten
 * above it.
 *
 * @param intervals gaps between ticks — one fewer than the number of ticks.
 */
export function niceAxisMax(rawMax: number, intervals: number): number {
  if (rawMax <= 0 || intervals <= 0) return intervals > 0 ? intervals : 1;
  const target = rawMax / intervals;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const normalised = target / magnitude;
  const step = (NICE_STEPS.find((s) => normalised <= s) ?? 10) * magnitude;
  return step * intervals;
}

/**
 * What the active formatter writes either side of a figure — `£` before, or
 * ` kr` after — read off its output for `1` rather than assumed to be a
 * leading `£`.
 */
function currencyAffixes(formatCurrency: (n: number) => string): { before: string; after: string } {
  const sample = formatCurrency(1);
  if (!/\d/.test(sample)) return { before: '£', after: '' };
  // Up to the first digit, and after the last — so a formatter that prints
  // `£1.00` contributes no `.00` to the suffix.
  return { before: sample.replace(/\d.*$/, ''), after: sample.replace(/^.*\d/, '') };
}

function trimZero(s: string): string {
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

/**
 * Compact currency for axis ticks / direct labels — "£0", "£200k", "-£4M".
 * The sign leads the symbol, as the full formatter's own output does.
 */
export function compactCurrency(value: number, formatCurrency: (n: number) => string): string {
  const abs = Math.abs(value);
  // `|| 0` turns the `-0` a small negative rounds to into 0, which prints "£0".
  if (abs < 1_000 && value !== 0) return formatCurrency(Math.round(value) || 0);
  // The unit is chosen from the rounded figure, so £999,750 reads £1M rather
  // than "£1000k"; tenths of a million round in integer space, off the float.
  const magnitude =
    Math.round(abs / 1_000) >= 1_000
      ? `${trimZero((Math.round(abs / 100_000) / 10).toFixed(1))}M`
      : abs >= 1_000
        ? `${Math.round(abs / 1_000)}k`
        : '0';
  const { before, after } = currencyAffixes(formatCurrency);
  return `${value < 0 ? '-' : ''}${before}${magnitude}${after}`;
}

function currentMonthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Percentage across the plot width where "today" falls, or `null` when today's
 * month is not in the window at all — in which case the caller draws no marker
 * rather than pinning one to an edge, which would place today outside the span
 * it is meant to sit inside.
 */
export function todayPositionPct(monthKeys: readonly string[]): number | null {
  const idx = monthKeys.indexOf(currentMonthKey());
  if (idx === -1) return null;
  const now = new Date();
  const daysInMonth = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const dayFrac = (now.getUTCDate() - 1) / daysInMonth;
  return ((idx + dayFrac) / monthKeys.length) * 100;
}
